import MuiModal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Button from '@components/Button';
import Tooltip from '@components/Tooltip';
import { MODAL_Z_INDEX } from '@constants/modal';
import ModalHeader from './ModalHeader';
import { PopupZIndexProvider, popupZIndexFor } from './popupLayer';
import useDialogLabels from './useDialogLabels';
import { ModalFooter, ModalFooterGroup } from './ModalFooter';
import withStopPropagation from './withStopPropagation';

const Modal = styled(MuiModal, {
    shouldForwardProp: prop => prop !== 'aboveModal',
})(({ aboveModal }) => ({
    left: 'var(--sidebar-width)',
    zIndex: aboveModal ? MODAL_Z_INDEX.aboveModal : MODAL_Z_INDEX.base,
    '@media (max-width: 1024px)': {
        left: 0,
    },
    '& .MuiBackdrop-root': {
        '@media (max-width: 1024px)': {
            left: 0,
        },
    },
}));

const StyledPaper = styled(Paper, {
    shouldForwardProp: prop => prop !== 'fullScreen',
})(({ fullScreen }) => ({
    width: 'auto',
    maxHeight: 'calc(var(--100vh) - 32px)',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 8,
    boxShadow:
        '0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2) !important',
    '@media (max-width: 600px)': {
        maxHeight: 'none',
        ...(fullScreen
            ? {
                  '&&&': {
                      width: '100%',
                      maxWidth: 'none',
                      height: 'var(--100vh)',
                      maxHeight: 'none',
                      borderRadius: 0,
                  },
              }
            : {
                  width: 'calc(100% - 32px)',
              }),
    },
}));

const Content = styled('div')({
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
});

const ModalShell = ({
    isOpen,
    children,
    handleClose,
    closeDisabled = false,
    onBack,
    aboveModal = false,
    className,
    fullScreen = false,
    title,
    subtitle,
    descriptionId,
    primaryButton: PrimaryButton = null,
    onPrimaryAction,
    primaryActionText,
    primaryActionType = 'contained',
    primaryActionDisabled,
    primaryActionTooltip = '',
    onSecondaryAction,
    secondaryActionText,
    secondaryActionDisabled = false,
    onAdditionalAction,
    additionalTextButton,
    additionalButtonType = 'danger',
    additionalActionDisabled = false,
    onSecondAdditionalAction,
    secondAdditionalTextButton,
    secondAdditionalButtonType = 'outlined',
    secondAdditionalActionDisabled = false,
    footerLeft = null,
}) => {
    const { headerProps, dialogProps } = useDialogLabels({ title, subtitle, descriptionId });
    const hasFooter =
        footerLeft ||
        onAdditionalAction ||
        onSecondAdditionalAction ||
        onSecondaryAction ||
        onPrimaryAction ||
        PrimaryButton;

    return (
        <Modal
            className={className}
            classes={{ root: 'modal-base-root' }}
            open={isOpen}
            onClose={closeDisabled ? undefined : handleClose}
            aboveModal={aboveModal}
        >
            <StyledPaper className="modal-base-paper" fullScreen={fullScreen} {...dialogProps}>
                <PopupZIndexProvider value={popupZIndexFor(aboveModal)}>
                    <ModalHeader
                        className="modal-base-header"
                        title={title}
                        subtitle={subtitle}
                        {...headerProps}
                        onBack={onBack}
                        onClose={handleClose}
                        closeDisabled={closeDisabled}
                    />
                    <Content className="modal-base-content">{children}</Content>
                    {hasFooter && (
                        <ModalFooter className="modal-base-footer" inlineOnMobile={Boolean(footerLeft)}>
                            <ModalFooterGroup className="modal-base-footer-btn-group">
                                {footerLeft}
                                {onAdditionalAction && (
                                    <Button
                                        variant={additionalButtonType}
                                        onClick={withStopPropagation(onAdditionalAction)}
                                        disabled={additionalActionDisabled}
                                    >
                                        {additionalTextButton}
                                    </Button>
                                )}
                                {onSecondAdditionalAction && (
                                    <Button
                                        variant={secondAdditionalButtonType}
                                        onClick={withStopPropagation(onSecondAdditionalAction)}
                                        disabled={secondAdditionalActionDisabled}
                                    >
                                        {secondAdditionalTextButton}
                                    </Button>
                                )}
                            </ModalFooterGroup>
                            <ModalFooterGroup className="modal-base-footer-btn-group">
                                {onSecondaryAction && (
                                    <Button
                                        variant="outlined"
                                        onClick={withStopPropagation(onSecondaryAction)}
                                        disabled={secondaryActionDisabled}
                                    >
                                        {secondaryActionText}
                                    </Button>
                                )}
                                {onPrimaryAction && (
                                    <Tooltip title={(primaryActionDisabled && primaryActionTooltip) || ''}>
                                        <span>
                                            <Button
                                                variant={primaryActionType}
                                                onClick={withStopPropagation(onPrimaryAction)}
                                                disabled={primaryActionDisabled}
                                            >
                                                {primaryActionText}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {PrimaryButton && !onPrimaryAction ? PrimaryButton : null}
                            </ModalFooterGroup>
                        </ModalFooter>
                    )}
                </PopupZIndexProvider>
            </StyledPaper>
        </Modal>
    );
};

export default ModalShell;
